import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import ky from 'ky';
import { api, ResponseMap } from '@/lib/utils/api.utils';
import { ERROR_CODE } from '@/lib/config/errorCode';
import { useSession } from '@/lib/hooks/useSession';
import { SERVER_CODE } from '@/lib/config/serverCode';
import { toaster } from '@/components/ui/toaster';
import { SwapType } from './trade.service';
import { useTranslations } from 'next-intl';
const requestUrl = process.env.NEXT_PUBLIC_REQUEST_URL;

interface ChainGasType {
  chainId: string;
  gasType: number;
  gasTypePromptInfo: string;
}

interface UserWallet {
  chainId: number;
  isUsing: number;
  tokenValue: number;
  transferInRemark: string;
  txAmount: string;
  userId: number;
  userWalletAddress: string;
  walletGroupId: number;
  walletId: number;
  walletTag: string;
}

interface WalletGroup {
  isUsing: number;
  userWallets: UserWallet[];
  wallGroupValues: number;
  walletGroupId: number;
  chainId: number;
}

interface UserAccountResponse {
  gasType: number;
  gasTypeBase: number;
  gasTypeBsc: number;
  gasTypeSol: number;
  gasTypeTrx: number;
  isAntiMev: boolean;
  isAutoSlippage: boolean;
  isFastTrade: number;
  slippage: number;
  totalChainGasType: ChainGasType[];
  totalValue: number;
  userWalletGroups: WalletGroup[];
}

export function useUserAccount() {
  const { data: sessionData } = useSession();
  return useQuery<UserAccountResponse, Error>({
    queryKey: ['userAccount'],
    queryFn: () => requestUserAccount(),
    enabled: !!sessionData?.isLogin,
  });
}

export async function requestUserAccount(): Promise<UserAccountResponse> {
  try {
    const response = await api.get(
      `${requestUrl}/v1/account/user_currency_list`,
    );

    if (!response.ok) {
      throw new Error(ERROR_CODE.NETWORK_ERROR);
    }

    const data = await response.json<ResponseMap<UserAccountResponse>>();

    if (!data.data) {
      throw new Error(ERROR_CODE.EMPTY_DATA);
    }

    return data.data;
  } catch (error) {
    console.error('Error requestUserAccount:', error);
    throw error;
  }
}

export type tradeAccountProps = {
  is_anti_mev: boolean;
  is_auto_slippage: boolean;
  slippage: string;
  gas_type_sol: string | number;
};

export async function requestSetTradeAccount(
  params: tradeAccountProps,
): Promise<{ code: number; message: string }> {
  const response = await api.post(`${requestUrl}/v1/account/trade_setting`, {
    json: params,
  });
  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }
  const data =
    await response.json<ResponseMap<{ code: number; message: string }>>();
  return { code: data.code, message: data.message };
}

export interface TokenTransferBody {
  email_code: string;
  code: string;
  wallet_group_id: number;
  chain_id: number;
  balance: string;
  recipient_address: string;
  token_address: string;
}

export interface TokenTransferResponse {
  data: {
    tx_hash: string;
  };
}

export async function requestTokenTransfer(
  params: TokenTransferBody,
): Promise<TokenTransferResponse | undefined> {
  const response = await api.post(`${requestUrl}/v1/account/token_transfer`, {
    json: params,
  });
  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }
  const data = await response.json<ResponseMap<TokenTransferResponse>>();

  if (data.code !== SERVER_CODE.SUCCESS) {
    throw new Error(data.message);
  }

  return data.data;
}

type PaymentListResponse = {
  /**
   * 支付地址列表
   */
  paymentList: PaymentList[];
  /**
   * 支付优先顺序, 1: USDT/USDC First 2: Native Gas Token First
   */
  tokenPriority: string;
};

export interface PaymentList {
  /**
   * 支付数量
   */
  fromAmount?: string;
  /**
   * 支付链id
   */
  fromChainId?: string;
  /**
   * 支付token地址, 原生币为空，usdt usdc 为对应的地址
   */
  fromTokenCa?: string;
  /**
   * 是否原生代币
   */
  isNative?: boolean;
  /**
   * token单位
   */
  symbol?: string;
  /**
   * 目标链id
   */
  toChainId?: number;
  /**
   * 总额USD
   */
  totalValueUsd?: string;
  /**
   * 目标token地址
   */
  toTokenCa?: string;
}

type PaymentListParams = {
  /**
   * 支付的链id。110000 为omniChain
   */
  payChainId: number;
  /**
   * token 地址
   */
  tokenAddress?: string;
  /**
   * token 的链id
   */
  tokenChainId: number;
  /**
   * 购买token总金额（数量） omniChain时单位是U
   */
  totalValue: string;
};

export async function fetchPaymentList(params: PaymentListParams) {
  const response = await api.get(`${requestUrl}/v3/account/get_payment_list`, {
    searchParams: params,
  });

  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }

  const data = await response.json<ResponseMap<PaymentListResponse>>();

  if (data.code !== SERVER_CODE.SUCCESS) {
    throw new Error(data.message);
  }

  return data.data;
}

export async function getPaymentList(
  params: PaymentListParams,
  swapType: number,
): Promise<PaymentList | undefined> {
  if (swapType === SwapType.BUY) {
    const paymentList = await fetchPaymentList(params);
    return paymentList?.paymentList?.[0];
  } else {
    return {
      fromAmount: params.totalValue,
      fromChainId: params.tokenChainId.toString(),
      fromTokenCa: '',
      isNative: true,
      symbol: '',
      toChainId: params.tokenChainId,
      totalValueUsd: '',
      toTokenCa: params.tokenAddress,
    };
  }
}

export interface TokenPriorityResponse {
  tokenPriority: string;
}

export async function requestTokenPriority(
  chainId: number,
): Promise<TokenPriorityResponse> {
  const response = await api.get(
    `${requestUrl}/v1/account/get_token_priority`,
    {
      searchParams: { chainId },
    },
  );

  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }

  const data = await response.json<ResponseMap<TokenPriorityResponse>>();

  if (!data.data) {
    throw new Error(ERROR_CODE.EMPTY_DATA);
  }

  if (data.code !== SERVER_CODE.SUCCESS) {
    throw new Error(data.message);
  }

  return data.data;
}

export function useTokenPriority(chainId: number) {
  const { data: sessionData } = useSession();
  return useQuery<TokenPriorityResponse, Error>({
    queryKey: ['tokenPriority', chainId],
    queryFn: () => requestTokenPriority(chainId),
    enabled: !!sessionData?.isLogin,
  });
}

export interface SetTokenPriorityParams {
  chainId: number;
  tokenPriority: number;
}

export async function requestSetTokenPriority(
  params: SetTokenPriorityParams,
): Promise<{ code: number; message: string }> {
  const response = await api.post(
    `${requestUrl}/v1/account/set_token_priority`,
    {
      json: params,
    },
  );

  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }

  const data = await response.json<ResponseMap<{}>>();

  if (data.code !== SERVER_CODE.SUCCESS) {
    throw new Error(data.message);
  }

  return { code: data.code, message: data.message };
}

export function useSetTokenPriority() {
  const queryClient = useQueryClient();
  const t = useTranslations('TradePage.TradingSideBar.AdvancedSettings');

  return useMutation({
    mutationFn: (params: SetTokenPriorityParams) =>
      requestSetTokenPriority(params),
    onMutate: (variables) => {
      const previousTokenPriority =
        queryClient.getQueryData<TokenPriorityResponse>([
          'tokenPriority',
          variables.chainId,
        ]);
      queryClient.cancelQueries({
        queryKey: ['tokenPriority', variables.chainId],
      });
      queryClient.setQueryData(['tokenPriority', variables.chainId], {
        tokenPriority: variables.tokenPriority.toString(),
      });
      return previousTokenPriority;
    },
    onSuccess: (_, variables) => {
      // 更新 tokenPriority 查询缓存
      queryClient.invalidateQueries({
        queryKey: ['tokenPriority', variables.chainId],
      });
      // 显示成功提示
      toaster.success({
        title: t('token_priority_success'),
        type: 'success',
      });
    },
    onError: (error: Error) => {
      toaster.error({
        title: error.message,
        type: 'error',
      });
    },
  });
}

export async function requestIsUserInWhiteList() {
  const response = await api.get(
    `${requestUrl}/v3/account/is_user_in_white_list`,
  );
  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }
  const data = await response.json<ResponseMap<{ result: boolean }>>();
  if (data.code !== SERVER_CODE.SUCCESS) {
    throw new Error(data.message);
  }
  return data.data?.result ?? false;
}

export function useIsUserInWhiteList() {
  const { data: sessionData } = useSession();
  return useQuery<boolean, Error>({
    queryKey: ['isUserInWhiteList'],
    queryFn: () => requestIsUserInWhiteList(),
    enabled: !!sessionData?.isLogin,
  });
}
export interface WalletLoginProps {
  inviteCode?: string;
  /**
   * 签名信息
   */
  signature: string;
  /**
   * 钱包类型，1:evm 2:sol 3:Bitcoin 默认为evm
   */
  signType?: number;
  /**
   * 时间戳
   */
  timestamp: number;
  /**
   * 钱包地址
   */
  walletAddress: string;
}
export interface WalletLoginResult {
  accessToken: string;
  address: string;
  expiresIn: string;
  listenKey: string;
  tokenType: string;
  user: UserAccountResponse;
}
export async function requestWalletLogin(
  params: WalletLoginProps,
): Promise<WalletLoginResult> {
  const response = await api.post(`${requestUrl}/v1/account/wallet_login`, {
    json: params,
  });
  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }
  const data = await response.json<ResponseMap<WalletLoginResult>>();
  if (data.code !== SERVER_CODE.SUCCESS) {
    throw new Error(data.message);
  }
  if (!data.data) {
    throw new Error('data is empty');
  }
  return data.data;
}

export interface ApkDownloadResponse {
  packageName: string;
  url: string;
}

export async function requestApkDownloadUrl(): Promise<ApkDownloadResponse> {
  try {
    const response = await ky.get(`${requestUrl}/v4/account/get_apk_download_url`);

    if (!response.ok) {
      throw new Error(ERROR_CODE.NETWORK_ERROR);
    }

    const data = await response.json<ResponseMap<ApkDownloadResponse>>();

    if (data.code !== SERVER_CODE.SUCCESS) {
      throw new Error(data.message);
    }

    if (!data.data) {
      throw new Error(ERROR_CODE.EMPTY_DATA);
    }

    return data.data;
  } catch (error) {
    console.error('Error requestApkDownloadUrl:', error);
    throw error;
  }
}

export function useApkDownloadUrl() {
  return useQuery<ApkDownloadResponse, Error>({
    queryKey: ['apkDownloadUrl'],
    queryFn: () => requestApkDownloadUrl(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
